import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import { Box, CssBaseline, Paper as MuiPaper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";

import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import Sidebar from "../components/sidebar/Sidebar";
import Footer from "../components/Footer";

import useAuth from "@/hooks/useAuth";
import useNavigationTree from "@/hooks/useNavigationTree";
import usePolling from "@/hooks/usePolling";
import { setNotifications, setUnreadCount } from "@/redux/slices/notifications";

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Dashboard = ({ children }) => {
  const { isAuthenticated, isInitialized, user } = useAuth();
  const router = useLocation();
  const dispatch = useDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigationgroupid = 1;
  const roleid = 1;
  const dashboardItems = useNavigationTree(navigationgroupid, roleid);

  // Poll notifications every 30s for the navbar bell (badge + dropdown list).
  // usePolling already pauses while the browser tab is hidden and skips work
  // when disabled. We only poll once we have an authenticated userid.
  const userid = user?.userid || 0;
  const notificationsPolling = useMemo(
    () => [
      {
        key: "items",
        endpoint: "notifications",
        action: "getNotifications",
        params: { onlyunread: false, limit: 25 },
        responseKey: "rows",
      },
      {
        key: "unreadCount",
        endpoint: "notifications",
        action: "getUnreadCount",
        params: {},
        responseKey: "unreadCount",
      },
    ],
    [userid],
  );
  const pollingEnabled = isAuthenticated && userid > 0;
  const { data: notificationData, pollNow } = usePolling(notificationsPolling, 30000, pollingEnabled);

  // usePolling intentionally skips the first tick, so fetch once on mount /
  // when polling becomes enabled to fill the bell without a 30s delay.
  useEffect(() => {
    if (pollingEnabled) pollNow();
  }, [pollingEnabled, pollNow]);

  // Mirror polled notification data into the Redux slice the navbar reads from.
  useEffect(() => {
    if (notificationData.items !== undefined) {
      dispatch(setNotifications(notificationData.items));
    }
  }, [notificationData.items, dispatch]);

  useEffect(() => {
    if (notificationData.unreadCount !== undefined) {
      dispatch(setUnreadCount(notificationData.unreadCount));
    }
  }, [notificationData.unreadCount, dispatch]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false);
  }, [router.pathname]);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar PaperProps={{ style: { width: drawerWidth } }} items={dashboardItems} />
        </Box>
      </Drawer>
      <AppContent>
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <MainContent px={isLgUp ? 10 : 5} py={isLgUp ? 5 : 5}>
          {children}
          <Outlet />
        </MainContent>
        <Footer />
      </AppContent>
    </Root>
  );
};

export default Dashboard;
